import { AxiosError } from 'axios';

import TableLayout from 'components/common/layout/table/TableLayout';
import AlertModal from 'components/common/modal/alert/AlertModal';
import ConfirmModal from 'components/common/modal/confirm/ConfirmModal';
import TableNoContent from 'components/common/no-content/TableNoContent';
import Pagination from 'components/common/pagination/Pagination';
import CommonTableHeader from 'components/common/table/header/CommonTableHeader';

import useCustomNavigate from 'hooks/common/useCustomNavigate';
import useModal from 'hooks/common/useModal';
import useCustomBoard from 'hooks/feature/menu/custom/useCustomBoard';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { RoutePath } from 'types/common/paths';
import { CustomBoard, CustomBoards } from 'types/feature/menu/customBoard';

import { MENU_CONTENTS_TABLE_HEADER } from './data/menuContentsTableHead';
import { CustomPageType } from 'types/feature/menu/customPage';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import MenuContentRows from './row/MenuContentRows';
import { handleSortOrderDesc } from 'utils/sort-order/handleSortOrderDesc';

type Props = {
  customBoards: CustomBoards | undefined;
  fetchCustomBoards: (customPageId: string, page?: number) => Promise<void>;
  pointId: string;
  pageId: string;
  customPageType: CustomPageType;
};

const MenuContentsTable = ({ customBoards, fetchCustomBoards, pointId, pageId, customPageType }: Props) => {
  const { customPageId } = useParams();
  const { deleteCustomBoard, putCustomBoard } = useCustomBoard();
  const { openConfirm, openModalPopup, closeModalPopup, openAlert } = useModal();
  const { customNavigate } = useCustomNavigate();

  const [message, setMessage] = useState('');
  const [deleteContentId, setDeleteContentId] = useState('');
  const [currentPage, setCurrentPage] = useState(0);

  const handleAlert = () => {
    openModalPopup('ALERT');
    setMessage('더 이상 순서를 변경할 수 없습니다.');
  };

  const onClickDelete = (e: React.MouseEvent<HTMLElement>, customBoardId: string) => {
    e.stopPropagation();

    openModalPopup('CONFIRM');
    setMessage('메뉴 콘텐츠를 삭제하시겠습니까?');

    setDeleteContentId(customBoardId);
  };

  const onClickOk = async () => {
    try {
      if (customPageId) {
        closeModalPopup('CONFIRM');

        await deleteCustomBoard(deleteContentId);
        await fetchCustomBoards(customPageId);

        openModalPopup('ALERT');
        setMessage('메뉴 콘텐츠가 삭제되었습니다.');
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400) {
          closeModalPopup('CONFIRM');

          openModalPopup('ALERT');
          setMessage('메뉴 콘텐츠를 삭제할 수 없습니다.');
        }
      }
    }
  };

  // 보드 상세 페이지로 이동
  const moveToBoardDetail = (boardId: string) => {
    customNavigate(RoutePath.pointCustomBoardDetail, pointId, pageId, boardId, {
      state: customPageType,
    });
  };

  // 보드 목록 페이지 이동
  const onChangePage = async (pageNum: number) => {
    if (customPageId) {
      setCurrentPage(pageNum);
      await fetchCustomBoards(customPageId, pageNum);
    }
  };

  const handleRow = async (dropResult: DropResult) => {
    try {
      if (customBoards) {
        const sourceIndex = dropResult.source.index;
        const currentItem = customBoards?.content[sourceIndex];

        const sortOrder = handleSortOrderDesc(dropResult, customBoards.content);

        if (sortOrder) {
          await putCustomBoard(currentItem.id, {
            ...currentItem,
            sortOrder,
          });
          if (customPageId) {
            await fetchCustomBoards(customPageId);
          }
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        console.error(e);
        handleAlert();
      }
    }
  };

  return (
    <>
      <TableLayout
        // 페이지네이션
        pagination={
          customBoards?.totalElements ? (
            <Pagination
              totalElements={customBoards?.totalElements}
              currentPage={currentPage}
              onChangePage={onChangePage}
            />
          ) : (
            <> </>
          )
        }
      >
        <>
          {/* 헤더 */}
          <CommonTableHeader tableHead={MENU_CONTENTS_TABLE_HEADER} />

          {/* 바디 */}

          <DragDropContext onDragEnd={handleRow}>
            <Droppable droppableId='table'>
              {provided => {
                return (
                  <tbody {...provided.droppableProps} ref={provided.innerRef}>
                    {customBoards?.content.length && customBoards?.content.length > 0 ? (
                      customBoards?.content
                        .sort((a: CustomBoard, b: CustomBoard) => (b.sortOrder || 0) - (a.sortOrder || 0))
                        .map((board, index) => (
                          <Draggable draggableId={`drag-${index}`} index={index} key={`drag-${index}`}>
                            {draggableProvided => (
                              <MenuContentRows
                                provided={draggableProvided}
                                moveToBoardDetail={moveToBoardDetail}
                                onClickDelete={onClickDelete}
                                board={board}
                                index={index}
                              />
                            )}
                          </Draggable>
                        ))
                    ) : (
                      <TableNoContent message='등록된 콘텐츠가 없습니다.' colSpan={6} />
                    )}
                    {provided.placeholder}
                  </tbody>
                );
              }}
            </Droppable>
          </DragDropContext>
        </>
      </TableLayout>

      {openConfirm && (
        <ConfirmModal
          message={message}
          onClickCancel={() => {
            closeModalPopup('CONFIRM');
          }}
          onClickOk={onClickOk}
        />
      )}

      {openAlert && (
        <AlertModal
          message={message}
          closeModal={() => {
            closeModalPopup('ALERT');
          }}
        />
      )}
    </>
  );
};
export default MenuContentsTable;
